import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Select, { MenuListProps } from "react-select";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/redux/store";
import {
  createInventoryTransferAsync,
  deleteItemInventoryTransferAsync,
  geteditInventoryTransferAsync,
  getInventoryVariantsListAsync,
  getReasonListAsync,
  getWarehouseListAsync,
  updateInventoryTransferAsync,
} from "src/redux/actions/omsPageActions";
import { toast } from "react-toastify";
import { formatDateForPayload, KTIcon } from "src/marketConnect/helpers";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";
import Loader from "src/utils/Loader";
import { components } from "react-select";
import ManageReasonModal from "../inventory-adjustment/ReasonModal";
import DatePicker, { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import {
  createInventoryItemSelectStyles,
  getInventoryItemMenuLayout,
  InventoryItemSelect,
} from "../inventory/inventoryItemSelectMenuProps";

registerLocale("en-GB", enGB);


interface Props {
  show: boolean;
  editData?: any;
  onClose: () => void;
  onSaved: (form: any) => void;
}

interface Option {
  value: number;
  label: string;
}

interface ItemOption {
  itemId?: number;
  label: string;
  value: string | number;
  quantity: number;
  unit: string;
  sku?: string;
  product_id?: number;
  source_total_quantity?: number;
  destination_total_quantity?: number;
}

interface TableItem {
  item: ItemOption | null;
  sourceStock: string;
  destinationStock: string;
  quantityTransfer: string;
  searchTerm: string;
  page: number;
  hasMore: boolean;
  options: ItemOption[];
  loading: boolean;
  transfer_item_id?: number;
}
type ReasonOption = { label: string; value: number };

const CreateEditTransferModal: React.FC<Props> = ({ show, onClose, editData, onSaved }) => {
  const dispatch = useDispatch<AppDispatch>();

  // ---------- state ----------
  const [form, setForm] = useState({
    transferOrder: "",
    date: new Date(),
    description: "",
    source: null as Option | null,
    destination: null as Option | null,
    items: [createEmptyRow()],
  });

  const [warehouseOptions, setWarehouseOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingInitiate, setLoadingInitiate] = useState(false);
  const isSubmittingRef = useRef(false);
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const isSynced = !!editData && editData?.sync_status === "SYNCED";
  // reason states
  const [reasonOptions, setReasonOptions] = useState<{ label: string; value: number }[]>([]);
  const [reason, setReason] = useState<{ label: string; value: number } | null>(null);
  const [reasonSearch, setReasonSearch] = useState("");
  const [reasonPage, setReasonPage] = useState(1);
  const [reasonHasMore, setReasonHasMore] = useState(true);
  const [reasonLoading, setReasonLoading] = useState(false);
  const reasonSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [showManageReason, setShowManageReason] = useState(false);
  const [editReasonId, setEditReasonId] = useState<string | null>(null);
  const isEditReasonApplied = useRef(false);

  // ---------- refs for debouncing ----------
  const warehouseSearchTimer = useRef<number | null>(null);
  const itemSearchTimers = useRef<Record<number, number | null>>({});
  const itemSelectAnchorRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [openItemSelectIndex, setOpenItemSelectIndex] = useState<number | null>(null);

  // ---------- helpers ----------
  function createEmptyRow(): TableItem {
    return {
      item: null,
      sourceStock: "",
      destinationStock: "",
      quantityTransfer: "",
      searchTerm: "",
      page: 1,
      hasMore: true,
      options: [],
      loading: false,
    };
  }

  const update = useCallback((key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateItem = useCallback((index: number, patch: Partial<TableItem>) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setForm((prev) => ({ ...prev, items: [...prev.items, createEmptyRow()] }));
  }, []);

  const handleRemoveRow = useCallback((index: number) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  }, []);

  // ---------- fetch warehouses ----------
  const fetchWarehouses = useCallback(
    async (search = "") => {
      setLoading(true);
      try {
        const res: any = await dispatch(
          getWarehouseListAsync({
            paramData: {
              page: 1, limit: 50, search, sort_by: "", warehouse_id: null, isActive: true,
            },
          })
        )

        const data =
          res?.payload?.results?.data?.map((wh: any) => ({
            value: wh.id, label: wh.api_name,
          })) || [];

        setWarehouseOptions(data);
      } catch (err: any) {
        console.error("fetchWarehouses err:", err);
        toast.error(err?.data?.message || "Failed to fetch warehouse locations");
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const fetchWarehousesDebounced = useCallback(
    (search = "") => {
      if (warehouseSearchTimer.current) window.clearTimeout(warehouseSearchTimer.current);
      // @ts-ignore - window.setTimeout returns number in browser
      warehouseSearchTimer.current = window.setTimeout(() => fetchWarehouses(search), 400);
    },
    [fetchWarehouses]
  );

  // ---------- fetch item options ----------
  const fetchItemOptions = useCallback(
    async (rowIndex: number, page = 1, search = "") => {
      // require source to fetch
      if (!form.source) return;
      // set loading
      updateItem(rowIndex, { loading: true });
      try {
        const res: any = await dispatch(
          getInventoryVariantsListAsync({
            page, limit: 10, search, sort_by: "asc", order_by: "id",
            source_warehouse_id: String(form.source.value), destination_warehouse_id: String(form.destination?.value ?? ""),
          })
        );

        const data = res?.payload?.results?.data || [];
        const isNext = !!res?.payload?.next;
        const newOptions: ItemOption[] = data.map((it: any) => ({
          label: it.name,
          value: it.id,
          sku: it.sku || "",
           product_id: it.platform_product?.id ?? it.platform_product ?? it.platform_product_id ?? 0,
           quantity: it.total_quantity,
          unit: it.unit || "",
          source_total_quantity: it.source_total_quantity,
          destination_total_quantity: it.destination_total_quantity,
        }));

        setForm((prev) => {
          const items = [...prev.items];
          const existing = items[rowIndex] || createEmptyRow();
          items[rowIndex] = {
            ...existing,
            options: page === 1 ? newOptions : [...(existing.options || []), ...newOptions],
            page,
            hasMore: isNext, loading: false,
          };
          return { ...prev, items };
        });
      } catch (err) {
        console.error("fetchItemOptions err:", err);
        updateItem(rowIndex, { loading: false });
      }
    },
    [dispatch, form.source, form.destination, updateItem]
  );

  const fetchItemOptionsDebounced = useCallback(
    (rowIndex: number, page = 1, search = "") => {
      // clear previous timer for this row
      if (itemSearchTimers.current[rowIndex]) window.clearTimeout(itemSearchTimers.current[rowIndex]!);
      // @ts-ignore
      itemSearchTimers.current[rowIndex] = window.setTimeout(() => fetchItemOptions(rowIndex, page, search), 400);
    },
    [fetchItemOptions]
  );

  // ---------- load warehouses on mount ----------
  useEffect(() => {
    fetchWarehouses("");
  }, []);

  // ---------- handle show / edit mode ----------
  useEffect(() => {
    if (!show) return;
    isEditReasonApplied.current = false;
    setReason(null);
    setEditReasonId(null)

    if (editData) {
      (async () => {
        try {
          setLoadingEditData(true);
          const res: any = await dispatch(geteditInventoryTransferAsync({ id: editData.id }));
          const data = res?.payload?.data;
          if (!data) return;

          const items: TableItem[] =
            (data.inventory_adjustments || []).map((item: any) => {
              // Source Stock → old_quantity_in_location
              const sourceStock = item.old_quantity_in_location != null ? String(item.old_quantity_in_location) : "N/A";
              // Transfer Quantity → adjustment_quantity
              const quantityTransfer = item.adjustment_quantity != null ? String(item.adjustment_quantity) : "N/A";
              // Destination Stock → current_quantity_in_location
              const destinationStock = item.current_quantity_in_location != null ? String(item.current_quantity_in_location) : "N/A";

              return {
                item: {
                  itemId: item?.id,
                  label: item.product_variant?.name || item.sku || "",
                  value: item?.product_variant?.id || "",
                  sku: item.product_variant?.sku || item.sku || "",
                  product_id: item.product_variant?.platform_product?.id ?? item.product_variant?.platform_product ?? item.product_variant?.platform_product_id,
                  quantity: item.quantity,
                  source_total_quantity: item.source_total_quantity ?? 0,
                  destination_total_quantity: item?.destination_total_quantity ?? 0,
                },
                transfer_item_id: item.id,
                sourceStock,
                destinationStock,
                quantityTransfer,
                searchTerm: "",
                page: 1,
                hasMore: true,
                options: [],
                loading: false,
              };
            }) || [];

          setForm({
            transferOrder: String(data.reference_number || ""),
            date: data.transaction_date ? new Date(data.transaction_date) : new Date(),
            description: data.description || "",
            source: { value: data.source_warehouse_data?.id, label: data.source_warehouse_data?.api_name },
            destination: { value: data.destination_warehouse_data?.id, label: data.destination_warehouse_data?.api_name },
            items: items.length ? items : [createEmptyRow()],
          });
          setEditReasonId(data?.reason ?? null);
          setReason(null);
          fetchReasons("", 1);

        } catch (err) {
          console.error("loadEdit err:", err);
        } finally {
          setLoadingEditData(false);
        }
      })();
    } else {
      // create mode: reset
      setForm({
        transferOrder: "",
        date: new Date(),
        description: "",
        source: null,
        destination: null,
        items: [createEmptyRow()],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editData]);

  useEffect(() => {
    if (!editReasonId) return;
    if (isEditReasonApplied.current) return;
    if (!reasonOptions.length) return;

    const matched = reasonOptions.find(
      (opt) => opt.label === editReasonId
    );

    if (matched) {
      setReason(matched);
      isEditReasonApplied.current = true; // 🔐 lock
    }
  }, [editReasonId, reasonOptions]);

  const fetchReasons = async (search = "", page = 1) => {
    if (reasonLoading) return;
    try {
      setReasonLoading(true);

      const res: any = await dispatch(
        getReasonListAsync({
          paramData: { page, limit: 10, search, isActive: true, },
        }));

      const data = res?.payload?.results?.data?.map((r: any) => ({
        label: r.api_name, value: r.id,
      })) || [];

      setReasonOptions(prev => page === 1 ? data : [...prev, ...data]);
      setReasonPage(page);
      setReasonHasMore(!!res?.payload?.next);
    } catch (err) {
      console.error("Reason fetch error", err);
      toast.error("Failed to fetch reasons");
    } finally {
      setReasonLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (reasonSearchTimeout.current) {
        clearTimeout(reasonSearchTimeout.current);
      }
    };
  }, []);

  const ReasonMenuList = (
    props: MenuListProps<ReasonOption, false>
  ) => {
    return (
      <>
        <components.MenuList {...props}>
          {props.children}
        </components.MenuList>

        {/* Manage button INSIDE dropdown */}
        <div
          onClick={() => setShowManageReason(true)}
          style={{
            borderTop: "1px solid #e5e7eb",
            padding: "10px 12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: 500,
            color: "#0d6efd",
            background: "#fff",
          }}
        >
          <KTIcon iconName="setting-2" className="fs-6" />
          Manage Reasons
        </div>
      </>
    );
  };

  // ---------- other handlers ----------
  const swapLocations = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      source: prev.destination,
      destination: prev.source,
      items: prev.items.map(() => createEmptyRow()),
    }));
  }, []);

  const handleValueChange = useCallback((index: number, key: string, value: string) => {
    updateItem(index, { [key]: value } as Partial<TableItem>);
  }, [updateItem]);

  const itemTableDisabled = useMemo(() => !form.source || !form.destination, [form.source, form.destination]);

  // ---------- submit ----------
  const handleSubmit = useCallback(
    async (type: "save" | "transfer") => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      type === "save" ? setLoadingSave(true) : setLoadingInitiate(true);

      const releaseSubmitting = (cooldownMs = 0) => {
        if (cooldownMs > 0) {
          setTimeout(() => {
            isSubmittingRef.current = false;
            setLoadingSave(false);
            setLoadingInitiate(false);
          }, cooldownMs);
        } else {
          isSubmittingRef.current = false;
          setLoadingSave(false);
          setLoadingInitiate(false);
        }
      };

      if (!form.transferOrder.trim()) {
        toast.error("Transfer Number is required");
        releaseSubmitting(4000);
        return;
      }
      if (!reason) {
        toast.error("Reason is required");
        releaseSubmitting(4000);
        return;
      }

      if (!form.source || !form.destination) {
        toast.error("Both Source and Destination is required");
        releaseSubmitting(4000);
        return;
      }
      if (form.items.some((row) => !row.item)) {
        toast.error("Atleast one Product is required");
        releaseSubmitting(4000);
        return;
      }

      const validItems = form.items.filter((row) => row.item && row.quantityTransfer && Number(row.quantityTransfer) > 0);

      if (validItems.length === 0) {
        toast.error("Please add at least one item with quantity");
        releaseSubmitting(4000);
        return;
      }

      const itemsPayload = validItems.map((row) => {
        const obj: any = {
          variant_id: row.item?.value,
          product_id: row.item?.product_id,
          sku: row.item?.sku,
          reason: reason?.label ?? null,
          quantity: Number(row.quantityTransfer),
        };
        if (editData?.id && row.transfer_item_id) {
          obj.transfer_item_id = row.transfer_item_id;
        }
        return obj;
      });

      const payload = {
        source_warehouse_id: form.source.value,
        destination_warehouse_id: form.destination.value,
        transfer_date: formatDateForPayload(form.date),
        description: form.description,
        reason: reason?.label,
        reference: form.transferOrder || "",
        items: itemsPayload,
        custom_fields: [],
      };

      try {
        let res: any;
        if (editData?.id) {
          res = await dispatch(updateInventoryTransferAsync({ id: editData.id, payloadupdate: payload }));
        } else {
          res = await dispatch(createInventoryTransferAsync(payload));
        }
        if (res?.payload?.status === 200 || res?.payload?.success) {
          toast.success(type === "save" ? "Inventory Transfer Saved" : "Inventory Transfer Initiated");
          onSaved(payload);
          setReason(null);
          onClose();
        } else {
          toast.error(res?.payload?.data?.message || "Failed to process transfer");
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong");
      } finally {
        releaseSubmitting(0);
      }
    },
    [dispatch, editData, reason, form, onClose, onSaved]
  );

  // ---------- delete handler (called from modal success) ----------
  const handleDeleteConfirmed = useCallback(async () => {
    if (pendingDeleteIndex === null) return;

    const row = form.items[pendingDeleteIndex];
    // If existing row (edit mode and transfer_item_id exists) call API first
    if (editData?.id && row.transfer_item_id) {
      try {
        const res: any = await dispatch(
          deleteItemInventoryTransferAsync({
            id: editData.id,
            transfer_itemId: row.transfer_item_id,
          })
        );

        // Check payload success or status
        const success = res?.payload?.success || res?.payload?.status === 200;
        if (!success) {
          toast.error(res?.payload?.data?.message || "Failed to delete item");
          return; // do not remove row
        }
        toast.success("Item deleted successfully");
      } catch (err) {
        console.error("deleteItem err:", err);
        toast.error("Failed to delete item");
        return; // do not remove row
      }
    }

    // For new rows or after successful delete -> remove from UI
    handleRemoveRow(pendingDeleteIndex);

    setPendingDeleteIndex(null);
    setShowDeleteModal(false);
  }, [dispatch, editData, form.items, handleRemoveRow, pendingDeleteIndex]);

  // ---------- render ----------
  return (
    <Modal show={show} centered size="lg" backdrop="static">
      <div
        className="d-flex justify-content-between align-items-center px-4 py-3"
        style={{ background: "#f8f9fa", borderBottom: "1px solid #e1e1e1", borderTopLeftRadius: "7px", borderTopRightRadius: "7px" }}
      >
        <h5 className="m-0 fw-bold">
          {editData ? (isSynced ? "View Transfer" : "Edit Transfer") : "Create Transfer"}
        </h5>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary border d-flex align-items-center justify-content-center"
            style={{ width: "80px", height: "34px", backgroundColor: "#e6e9ecff", borderColor: "#6c757d" }}
            onClick={onClose}
            disabled={loadingInitiate}
          >
            Cancel
          </button>

          {!isSynced && <button
            className="btn btn-primary d-flex align-items-center justify-content-center"
            style={{ width: "80px", height: "34px" }}
            disabled={loadingSave}
            onClick={() => handleSubmit("save")}
          >
            {loadingSave ? "Saving..." : "Save"}
          </button>}
        </div>
      </div>

      <div className="px-4 py-4">
        {loadingEditData ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <Loader variant="section" />
          </div>
        ) : (
          <>
            {/* TRANSFER ORDER */}
            <div className="mb-3">
              <label className="fw-semibold required">Transfer Number</label>
              <input
                type="text"
                className="form-control"
                value={form.transferOrder}
                onChange={(e) => update("transferOrder", e.target.value)}
                style={{ width: "30rem", height: "38px" }}
                disabled={isSynced}
              />
            </div>

            {/* DATE */}
            <div className="mb-3">
              <label className="fw-semibold">Date</label><br />
              <DatePicker
                selected={form.date}
                onChange={(date) => {
                  if (date) setForm({ ...form, date });
                }}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select date"
                locale="en-GB"
                className="form-control"
                wrapperClassName="custom-datepicker-wrapper"
                readOnly
                disabled={true}
              />
              <style>
                {`.custom-datepicker-wrapper .react-datepicker__input-container {
                          width: 30rem !important;
                          height: "38px" !important;
                           }
                           `}
              </style>
            </div>


            {/* DESCRIPTION */}
            <div className="mb-3">
              <label className="fw-semibold">Description</label>
              <textarea className="form-control" placeholder="max 500 character" disabled={isSynced} value={form.description} maxLength={500} onChange={(e) => update("description", e.target.value)} style={{ width: "30rem" }} />
            </div>

            <div className="mb-4" style={{ width: "30rem" }}>
              <label className="fw-semibold required">Reason</label>
              <Select<ReasonOption, false>
                value={reason}
                options={reasonOptions}
                placeholder="Select reason"
                isDisabled={isSynced}
                isClearable
                isSearchable
                isLoading={reasonLoading}
                filterOption={null}
                inputValue={reasonSearch}
                components={{ MenuList: ReasonMenuList }}

                onChange={(val) => setReason(val)}

                onInputChange={(input, { action }) => {
                  if (action !== "input-change") return;

                  setReasonSearch(input);
                  setReasonPage(1);
                  setReasonHasMore(true);

                  if (reasonSearchTimeout.current) {
                    clearTimeout(reasonSearchTimeout.current);
                  }

                  reasonSearchTimeout.current = setTimeout(() => {
                    fetchReasons(input, 1);
                  }, 400);
                }}

                onMenuOpen={() => {
                  if (!reasonLoading && reasonPage === 1) {
                    fetchReasons(reasonSearch || "", 1);
                  }
                }}

                onMenuScrollToBottom={() => {
                  if (!reasonHasMore || reasonLoading) return;
                  fetchReasons(reasonSearch, reasonPage + 1);
                }}
              />
            </div>

            {/* SOURCE + DESTINATION */}
            <div className="row align-items-end mb-4">
              <div className="col-5">
                <label className="fw-semibold required">Source Warehouse</label>
                <Select
                  value={form.source}
                  onChange={(val) => update("source", val)}
                  options={warehouseOptions}
                  isLoading={loading}
                  placeholder="Select source warehouse"
                  onInputChange={(val) => fetchWarehousesDebounced(val)}
                  styles={{}}
                  isDisabled={isSynced}
                />
              </div>

              <div className="col-2 text-center">
                <button
                  className="btn btn-light border rounded-circle mt-4"
                  style={{ width: 35, height: 35, padding: 0, lineHeight: 0, fontSize: "20px" }}
                  onClick={swapLocations}
                  disabled={isSynced}
                >
                  ⇄
                </button>
              </div>

              <div className="col-5">
                <label className="fw-semibold required">Destination Warehouse</label>
                <Select
                  value={form.destination}
                  onChange={(val) => update("destination", val)}
                  options={warehouseOptions}
                  isLoading={loading}
                  placeholder="Select destination warehouse"
                  onInputChange={(val) => fetchWarehousesDebounced(val)}
                  isDisabled={isSynced}
                />
              </div>
            </div>

            {/* ITEM TABLE */}
            <div className="mb-3">
              <h6 className="fw-bold">Item Table</h6>
              <div style={{ pointerEvents: itemTableDisabled ? "none" : "auto", opacity: itemTableDisabled ? 0.5 : 1 }}>
                <style>
                  {`
                  .inventory-item-table-scroll {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                  }
                  .inventory-item-table-scroll .inventory-item-table {
                    width: 100%;
                    min-width: 48rem;
                    margin-bottom: 0;
                  }
                  `}
                </style>
                <div className="inventory-item-table-scroll">
                  <table className="table table-bordered inventory-item-table mb-0">
                  <thead>
                    <tr className="text-center">
                      <th className="text-nowrap" style={{ minWidth: 280 }}>Item Details <span className="text-danger">*</span></th>
                      <th style={{ width: 220, minWidth: 220 }}>Current Availability</th>
                      <th className="text-nowrap" style={{ width: 150, minWidth: 150 }}>Transfer Quantity <span className="text-danger">*</span></th>
                      <th style={{ width: 52, minWidth: 52 }} aria-label="Actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((row, index) => {
                      const isItemMenuOpen = openItemSelectIndex === index;
                      const itemMenuLayout = isItemMenuOpen
                        ? getInventoryItemMenuLayout(itemSelectAnchorRefs.current[index])
                        : null;
                      const getItemAnchor = () => itemSelectAnchorRefs.current[index] ?? null;

                      return (
                      <tr key={index}>
                        {/* ITEM SELECT */}
                        <td style={{ minWidth: 280 }}>
                          <div ref={(el) => { itemSelectAnchorRefs.current[index] = el; }}>
                          <InventoryItemSelect<ItemOption>
                            value={row.item}                     //  must be full option object
                            isDisabled={isSynced}
                            options={row.options}
                            placeholder="Search & Select Item"
                            isClearable
                            isLoading={row.loading}
                            filterOption={null}
                            inventoryItemAnchorGetter={getItemAnchor}
                            menuIsOpen={isItemMenuOpen}
                            maxMenuHeight={itemMenuLayout?.maxMenuHeight}
                            menuPlacement={itemMenuLayout?.menuPlacement ?? 'auto'}
                            /*  Ensures stable identity */
                            getOptionValue={(option) => String(option.value)}
                            getOptionLabel={(option) => option.label}
                            formatOptionLabel={(option: any) => {
                              if (option.sku && !option.label.includes(option.sku)) {
                                return `${option.label} (${option.sku})`;
                              }
                              return option.label;
                            }}
                            styles={createInventoryItemSelectStyles(getItemAnchor, {
                              singleValue: (provided) => ({
                                ...provided,
                                whiteSpace: "normal",
                              }),
                            })}
                            /*  Load first page when menu opens */
                            onMenuOpen={() => {
                              setOpenItemSelectIndex(index);
                              if (row.options.length === 0 && !row.loading) {
                                fetchItemOptions(index, 1, "");
                              }
                            }}
                            onMenuClose={() => {
                              if (openItemSelectIndex === index) {
                                setOpenItemSelectIndex(null);
                              }
                            }}
                            /*  Infinite scroll */
                            onMenuScrollToBottom={() => {
                              if (row.hasMore && !row.loading) {
                                fetchItemOptions(index, row.page + 1, row.searchTerm);
                              }
                            }}

                            /*  Selection handler */
                            onChange={(val) => {
                              const itemsCopy = [...form.items];
                              // Clear selection
                              if (!val) {
                                itemsCopy[index] = {
                                  ...itemsCopy[index],
                                  item: null,
                                  sourceStock: "",
                                  destinationStock: "",
                                  quantityTransfer: "",
                                  searchTerm: "", // important
                                };
                                setForm((prev) => ({ ...prev, items: itemsCopy }));
                                return;
                              }
                              //  Prevent duplicate items
                              const duplicateIndex = form.items.findIndex(
                                (r, i) => i !== index && r.item?.value === val.value
                              );
                              if (duplicateIndex !== -1) {
                                toast.error(`Item already selected in Row ${duplicateIndex + 1}`);
                                return;
                              }
                              //  Set selected item
                              itemsCopy[index] = {
                                ...itemsCopy[index],
                                item: val,
                                sourceStock: String(val.source_total_quantity ?? ""),
                                destinationStock: String(val.destination_total_quantity ?? ""),
                                quantityTransfer: "",
                                searchTerm: "", //  clear input so label shows
                              };

                              setForm((prev) => ({ ...prev, items: itemsCopy }));
                            }}


                            /*  Controlled input ONLY while typing */
                            inputValue={row.searchTerm || ""}
                            onInputChange={(inputValue, { action }) => {
                              if (action === "input-change") {
                                setForm((prev) => {
                                  const itemsCopy = [...prev.items];
                                  itemsCopy[index] = {
                                    ...itemsCopy[index],
                                    searchTerm: inputValue,
                                    page: 1,
                                    hasMore: true,
                                    options: [],
                                  };
                                  return { ...prev, items: itemsCopy };
                                });

                                fetchItemOptionsDebounced(index, 1, inputValue);
                              }
                            }}
                          />
                          </div>
                        </td>

                        {/* CURRENT AVAILABILITY */}
                        <td style={{ minWidth: 220 }}>
                          <div className="d-flex justify-content-between">
                            <div style={{ width: "40%" }}>
                              <strong>Source Stock:</strong>
                              <div>{row.sourceStock || "0.00"} Units</div>
                            </div>
                            <div style={{ width: "60%", borderLeft: "1px solid #ddd", paddingLeft: "10px" }}>
                              <strong>Destination Stock:</strong>
                              <div>{row.destinationStock}</div>
                            </div>
                          </div>
                        </td>

                        {/* TRANSFER QTY */}
                        <td className="text-center align-middle" style={{ minWidth: 150 }}>
                          <input
                            type="number"
                            className="form-control"
                            value={row.quantityTransfer}
                            min="0"
                            onChange={(e) => handleValueChange(index, "quantityTransfer", e.target.value)}
                            onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                            disabled={isSynced}
                          />
                        </td>

                        <td
                          className="text-center align-middle"
                          style={{ width: 52, minWidth: 52, padding: "0.5rem 0.25rem", verticalAlign: "middle" }}
                        >
                          {form.items.length > 1 && !isSynced ? (
                            <button
                              type="button"
                              className="btn btn-icon btn-bg-light btn-color-danger btn-sm"
                              title="Remove row"
                              onClick={() => {
                                const currentRow = form.items[index];

                                if (form.items.length === 1) {
                                  toast.error("At least one item is required");
                                  return;
                                }

                                if (!currentRow.transfer_item_id) {
                                  const updated = form.items.filter((_, i) => i !== index);
                                  setForm({ ...form, items: updated });
                                  return;
                                }

                                setPendingDeleteIndex(index);
                                setShowDeleteModal(true);
                              }}
                            >
                              <KTIcon iconName="trash" className="fs-4" />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
                </div>

                {!isSynced && (
                  <button type="button" className="btn btn-primary mt-3" onClick={handleAddRow}>
                    + Add Row
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ManageReasonModal
        show={showManageReason}
        onClose={() => setShowManageReason(false)}
        onSelect={(val) => {
          setReason(val);
          setShowManageReason(false);
          fetchReasons("", 1);
        }}
      />

      <CommonAlertModal
        title="Delete Item?"
        subtitle="This item will be removed from the transfer."
        isVisible={showDeleteModal}
        isTermsVisible={false}
        onSuccess={handleDeleteConfirmed}
        onCancel={() => {
          setPendingDeleteIndex(null);
          setShowDeleteModal(false);
        }}
        successBtnTitle="Delete"
        cancelBtnTitle="Cancel"
        successBtnClasses="btn btn-sm btn-danger m-2"
      />
    </Modal>
  );
};

export default CreateEditTransferModal;
